import { useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { formatoPorcentaje } from '@/lib/utils/formato-precio';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type { Action, RuleScope, ScopeProduct } from '../types/pricing-rules.types';
import { usePedidoConEspera } from '../lib/use-pedido-con-espera';
import { SelectorDeProductos, type FuenteDeLaLista } from './selector-de-productos';

/**
 * Las excepciones (maqueta S3): buscar un producto para sacarlo, con su margen
 * a la vista, y «volver a incluir». `GET /scope/products` trae el margen desde
 * el 4B-datos (T6); sin costo, «margen —». Para sacar muchos, «Elegir de la
 * lista…» abre el selector de pantalla completa (Sebastián, 27-sep).
 */
export function BuscadorDeExcepciones({
  excluidos,
  onChange,
  acciones,
  alcanceSinExcepciones,
  deDonde = 'la regla',
  fuente,
}: {
  excluidos: string[];
  onChange: (skus: string[]) => void;
  /**
   * Reglas: el límite del borrador y lo que alcanza antes de sacar nada, que es
   * lo que lista «Elegir de la lista…» (`/simular`). Una estrategia no los tiene
   * y pasa `fuente` (plan 3, T5b). Sin ninguno de los dos no se ofrece la lista.
   */
  acciones?: Action[];
  alcanceSinExcepciones?: RuleScope;
  /** De qué se saca: «la regla» (4B) o «la estrategia» (el paso 1 del plan 3). */
  deDonde?: string;
  /** Plan 3, P3-D12: la lista desde otra fuente (una estrategia). Manda sobre `acciones`. */
  fuente?: FuenteDeLaLista;
}) {
  const conLista = fuente != null || (acciones != null && alcanceSinExcepciones != null);
  const [q, setQ] = useState('');
  const [enLista, setEnLista] = useState(false);
  // Lo que ya se vio de cada producto, para mostrar nombre y margen de los sacados.
  const conocidos = useRef(new Map<string, ScopeProduct>());
  const pedido = usePedidoConEspera(q.trim().length >= 2 ? q.trim() : null, () =>
    pricingRulesApi.searchProducts(q.trim())
  );
  const resultados = pedido.estado === 'LISTO' ? pedido.data : [];
  for (const p of resultados) conocidos.current.set(p.sku, p);

  // Del selector llega sin costo (`/simular` no lo trae): sin dato no se dice nada.
  const margen = (p: ScopeProduct | undefined) =>
    p?.margen === undefined
      ? ''
      : p.margen === null
        ? 'margen —'
        : `margen ${formatoPorcentaje(p.margen)}`;

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          aria-label="Buscar un producto para sacarlo"
          placeholder={`buscar por nombre o código para sacarlo de ${deDonde}`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {conLista && (
          <Button type="button" variant="outline" onClick={() => setEnLista(true)}>
            Elegir de la lista…
          </Button>
        )}
      </div>
      {conLista && (
        <SelectorDeProductos
          abierto={enLista}
          titulo="Elegir las excepciones"
          descripcion={`Los productos que alcanza ${deDonde}. Los que marques no entran, aunque estén en el alcance.`}
          {...(fuente ? { fuente } : { acciones: acciones!, scope: alcanceSinExcepciones! })}
          elegidos={excluidos}
          textoConfirmar={(n) =>
            n === 0 ? 'Sin excepciones' : n === 1 ? 'Sacar 1 producto' : `Sacar ${n} productos`
          }
          onConfirmar={(skus, vistos) => {
            for (const p of vistos)
              if (!conocidos.current.has(p.sku)) conocidos.current.set(p.sku, p);
            onChange(skus);
            setEnLista(false);
          }}
          onCerrar={() => setEnLista(false)}
        />
      )}
      {pedido.estado === 'CARGANDO' && <p className="text-xs text-muted-foreground">Buscando…</p>}
      {pedido.estado === 'ERROR' && (
        <p className="text-xs text-destructive">No se pudo buscar. Probá de nuevo.</p>
      )}
      {resultados.filter((p) => !excluidos.includes(p.sku)).length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded-md border text-sm">
          {resultados
            .filter((p) => !excluidos.includes(p.sku))
            .map((p) => (
              <li
                key={p.sku}
                className="flex items-center justify-between gap-3 border-b px-3 py-1.5 last:border-b-0"
              >
                <span>
                  <strong>{p.name}</strong> <span className="text-muted-foreground">· {p.sku}</span>
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">{margen(p)}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onChange([...excluidos, p.sku]);
                    setQ('');
                  }}
                >
                  sacar
                </Button>
              </li>
            ))}
        </ul>
      )}
      {excluidos.length > 0 && (
        <ul className="max-h-72 overflow-y-auto rounded-md border text-sm" aria-label="Excepciones">
          {excluidos.map((sku) => {
            const p = conocidos.current.get(sku);
            return (
              <li
                key={sku}
                className="flex items-center justify-between gap-3 border-b px-3 py-1.5 last:border-b-0"
              >
                <span>
                  <strong>{p?.name ?? sku}</strong>
                  {p && <span className="text-muted-foreground"> · {sku}</span>}
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">{margen(p)}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onChange(excluidos.filter((s) => s !== sku))}
                >
                  volver a incluir
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
